import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useSettings } from "../contexts/SettingsContext";
import { Zap, X } from "lucide-react";

/**
 * Editable announcement bar shown above the public header.
 *
 * Renders two layers:
 *   1. A rotating "carousel" line — one message at a time, fades after `rotateMs`.
 *   2. A continuous marquee ticker that scrolls right-to-left underneath
 *      (only rendered when `promo_strip_marquee_text` is non-empty).
 *
 * Settings keys (managed in Admin → Settings → Promo strip):
 *   promo_strip_enabled        : bool       master toggle
 *   promo_messages             : list[ {text, link?, icon?, kind?} ]
 *   promo_strip_marquee_text   : str        ticker text (use "  ·  " between items)
 *   promo_strip_marquee_speed  : int        px/sec scroll speed (default 40)
 *   promo_strip_bg             : str        tailwind colour token, default "sky-700"
 *
 * `icon` ∈ {"flash", "none"}; `kind` ∈ {"primary", "accent"}.
 */
export default function PromoStrip() {
    const { settings } = useSettings();
    const [activeIdx, setActiveIdx] = useState(0);
    const [closed, setClosed] = useState(() => {
        try { return sessionStorage.getItem("hpl_promo_closed_v1") === "1"; }
        catch { return false; }
    });

    const enabled = settings?.promo_strip_enabled === true;
    const messages = Array.isArray(settings?.promo_messages) ? settings.promo_messages.filter(m => m && m.text) : [];
    const ticker = (settings?.promo_strip_marquee_text || "").trim();
    const speed = Math.max(10, Number(settings?.promo_strip_marquee_speed) || 40);
    const bg = settings?.promo_strip_bg || "sky-700";
    // marquee duration is recomputed only when ticker / speed change
    const tickerDurationSec = React.useMemo(
        () => Math.max(15, (ticker.length * 24) / speed),
        [ticker, speed],
    );

    useEffect(() => {
        if (!enabled || messages.length <= 1) return;
        const id = setInterval(() => setActiveIdx((i) => (i + 1) % messages.length), 4500);
        return () => clearInterval(id);
    }, [enabled, messages.length]);

    if (!enabled || closed || (messages.length === 0 && !ticker)) return null;

    const close = () => {
        setClosed(true);
        try { sessionStorage.setItem("hpl_promo_closed_v1", "1"); } catch { /* ok */ }
    };

    // Tailwind safelist — sky-700, amber-600, emerald-700, rose-700 are the supported tokens
    const bgClass = ({
        "sky-700": "bg-sky-700",
        "amber-600": "bg-amber-600",
        "emerald-700": "bg-emerald-700",
        "rose-700": "bg-rose-700",
        "stone-900": "bg-stone-900",
    })[bg] || "bg-sky-700";

    const active = messages[activeIdx];

    return (
        <div className={`${bgClass} text-stone-50 relative`} data-testid="promo-strip">
            {messages.length > 0 && (
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex items-center justify-center gap-2 text-[12px] sm:text-sm font-medium">
                    <PromoIcon icon={active?.icon} kind={active?.kind} />
                    {active?.link ? (
                        <Link to={active.link} className="hover:underline" data-testid="promo-strip-link">
                            {active.text}
                        </Link>
                    ) : (
                        <span>{active.text}</span>
                    )}
                    {messages.length > 1 && (
                        <span className="ml-2 hidden sm:inline-flex gap-1" aria-hidden="true">
                            {messages.map((_, i) => (
                                <button
                                    key={i}
                                    onClick={() => setActiveIdx(i)}
                                    className={`w-1.5 h-1.5 rounded-full transition-opacity ${i === activeIdx ? "bg-white opacity-100" : "bg-white opacity-40"}`}
                                    aria-label={`Show message ${i + 1}`}
                                    data-testid={`promo-strip-dot-${i}`}
                                />
                            ))}
                        </span>
                    )}
                </div>
            )}
            {ticker && (
                <div className="bg-black/15 overflow-hidden border-t border-white/10">
                    <div
                        className="flex whitespace-nowrap py-1 text-[11px] sm:text-xs"
                        style={{ animation: `hpl-promo-marquee ${tickerDurationSec}s linear infinite` }}
                        data-testid="promo-strip-ticker"
                    >
                        <span className="px-8">{ticker}</span>
                        <span className="px-8" aria-hidden="true">{ticker}</span>
                        <span className="px-8" aria-hidden="true">{ticker}</span>
                    </div>
                </div>
            )}
            <button
                onClick={close}
                className="absolute top-1 right-1 sm:top-1.5 sm:right-2 text-stone-100/80 hover:text-white p-1"
                aria-label="Dismiss announcement"
                data-testid="promo-strip-close"
            >
                <X className="w-3.5 h-3.5" />
            </button>
            <style>{`
                @keyframes hpl-promo-marquee {
                    0%   { transform: translateX(0%); }
                    100% { transform: translateX(-33.333%); }
                }
            `}</style>
        </div>
    );
}

function PromoIcon({ icon, kind }) {
    if (icon === "none") return null;
    const cls = kind === "accent" ? "text-amber-200" : "text-yellow-300";
    return <Zap className={`w-3.5 h-3.5 ${cls} drop-shadow`} fill="currentColor" />;
}
